import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Shield, Eye, Building2, Calendar, FileText, Users, MapPin, Activity, AlertTriangle, Star, ShieldCheck, Package, Euro } from "lucide-react";
import { useTargetAdminCustomization } from "@/hooks/useTargetAdminCustomization";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ModuleSettingsProps {
  adminId: string;
}

interface Module {
  name: string;
  label: string;
  description: string;
  icon: any;
  color: string;
}

const AVAILABLE_MODULES: Module[] = [
  {
    name: "sentinel",
    label: "Sentinel",
    description: "Patrol observations and emergency reports",
    icon: Eye,
    color: "text-blue-500"
  },
  {
    name: "facility_presence",
    label: "Facility Presence",
    description: "Site management and checkpoint tracking",
    icon: Building2,
    color: "text-green-500"
  },
  {
    name: "scheduling",
    label: "Scheduling",
    description: "Team schedules and shift management",
    icon: Calendar,
    color: "text-purple-500"
  },
  {
    name: "reports",
    label: "Reports",
    description: "Generate and view reports",
    icon: FileText,
    color: "text-orange-500"
  },
  {
    name: "hr_management",
    label: "HR Management",
    description: "Candidate tracking and recruitment",
    icon: Users,
    color: "text-pink-500"
  },
  {
    name: "missions",
    label: "Missions",
    description: "Mission planning and tracking",
    icon: MapPin,
    color: "text-cyan-500"
  },
  {
    name: "performance",
    label: "Performance",
    description: "Employee evaluations and awards",
    icon: Activity,
    color: "text-yellow-500"
  },
  {
    name: "training",
    label: "Training",
    description: "Training modules and certificates",
    icon: Shield,
    color: "text-red-500"
  },
  {
    name: "whistleblowers",
    label: "Whistleblowers",
    description: "Anonymous reporting system",
    icon: AlertTriangle,
    color: "text-amber-500"
  },
  {
    name: "provider_evaluations",
    label: "Provider Evaluations",
    description: "Client provider evaluations and analytics",
    icon: Star,
    color: "text-indigo-500"
  },
  {
    name: "finance",
    label: "Finance",
    description: "Financial settings and cost management",
    icon: Euro,
    color: "text-emerald-500"
  },
  {
    name: "ethics_compliance",
    label: "Ethics & Compliance",
    description: "Company policies and code of conduct",
    icon: Shield,
    color: "text-indigo-600"
  },
  {
    name: "equipment_management",
    label: "Equipment Management",
    description: "Protective equipment inventory and tracking",
    icon: Package,
    color: "text-orange-600"
  },
  {
    name: "supplier_evaluations",
    label: "Supplier Evaluations",
    description: "Supplier performance and ISO 9001 compliance",
    icon: ShieldCheck,
    color: "text-emerald-600"
  }
];

export default function ModuleSettings({ adminId }: ModuleSettingsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { customization, saveWhistleblowerUrl } = useTargetAdminCustomization(adminId);
  const { isSuperAdmin } = useUserRoles();
  const [whistleblowerUrl, setWhistleblowerUrl] = useState('');

  // Update whistleblowerUrl when customization loads
  useEffect(() => {
    if (customization?.whistleblower_url) {
      setWhistleblowerUrl(customization.whistleblower_url);
    }
  }, [customization]);

  // Fetch current module settings
  const { data: moduleSettings, isLoading } = useQuery({
    queryKey: ["moduleSettings", adminId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_module_settings")
        .select("*")
        .eq("admin_id", adminId);
      
      if (error) throw error;
      
      // Create a map of module states
      const settingsMap = new Map(
        data?.map(setting => [setting.module_name, setting.is_enabled]) || []
      );
      
      // If no settings exist, all modules are enabled by default
      return AVAILABLE_MODULES.map(module => ({
        ...module,
        isEnabled: settingsMap.has(module.name) ? settingsMap.get(module.name) : true
      }));
    },
    enabled: !!adminId,
  });

  // Mutation to toggle module
  const toggleModuleMutation = useMutation({
    mutationFn: async ({ moduleName, isEnabled }: { moduleName: string; isEnabled: boolean }) => {
      const { error } = await supabase
        .from("admin_module_settings")
        .upsert(
          {
            admin_id: adminId,
            module_name: moduleName,
            is_enabled: isEnabled,
          },
          { onConflict: "admin_id,module_name" }
        );
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate both the settings view AND the module access hook cache
      queryClient.invalidateQueries({ queryKey: ["moduleSettings", adminId] });
      queryClient.invalidateQueries({ queryKey: ["userModuleAccess", adminId] });
      // Also invalidate for any users created by this admin
      queryClient.invalidateQueries({ queryKey: ["userModuleAccess"] });
      toast({
        title: "Module updated",
        description: "Module settings have been saved successfully. Please refresh the page to see changes.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleToggle = (moduleName: string, currentState: boolean) => {
    toggleModuleMutation.mutate({
      moduleName,
      isEnabled: !currentState,
    });
  };

  const handleSaveWhistleblowerUrl = async () => {
    await saveWhistleblowerUrl(whistleblowerUrl);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Module Settings</CardTitle>
          <CardDescription>Loading module settings...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enabled Modules</CardTitle>
        <CardDescription>
          Control which features and pages are available for this admin account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {moduleSettings?.map((module) => (
            <div
              key={module.name}
              className={`flex items-start justify-between space-x-4 rounded-lg border p-4 ${
                module.name === 'whistleblowers' && module.isEnabled ? 'md:col-span-2' : ''
              }`}
            >
              <div className="flex items-start space-x-4 flex-1">
                <module.icon className={`h-5 w-5 mt-0.5 ${module.color}`} />
                <div className="space-y-3 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <Label
                        htmlFor={module.name}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {module.label}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {module.description}
                      </p>
                    </div>
                    <Switch
                      id={module.name}
                      checked={module.isEnabled}
                      onCheckedChange={() => handleToggle(module.name, module.isEnabled)}
                      disabled={toggleModuleMutation.isPending}
                    />
                  </div>
                  
                  {/* Show URL input when whistleblowers is enabled AND user is super admin */}
                  {module.name === 'whistleblowers' && module.isEnabled && isSuperAdmin && (
                    <div className="space-y-2 pt-2 border-t">
                      <Label htmlFor="whistleblower-url" className="text-xs text-muted-foreground">
                        Redirect URL (Super Admin Only)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="whistleblower-url"
                          type="url"
                          placeholder="https://trust.shiftask.app/"
                          value={whistleblowerUrl}
                          onChange={(e) => setWhistleblowerUrl(e.target.value)}
                          className="flex-1"
                        />
                        <Button 
                          size="sm" 
                          onClick={handleSaveWhistleblowerUrl}
                          disabled={!whistleblowerUrl || whistleblowerUrl === customization?.whistleblower_url}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Show read-only URL info for non-super admins */}
                  {module.name === 'whistleblowers' && module.isEnabled && !isSuperAdmin && customization?.whistleblower_url && (
                    <div className="space-y-1 pt-2 border-t">
                      <Label className="text-xs text-muted-foreground">
                        Redirect URL
                      </Label>
                      <p className="text-sm text-foreground/70 break-all">
                        {customization.whistleblower_url}
                      </p>
                      <p className="text-xs text-muted-foreground italic">
                        Only the Super Admin can modify this URL
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
